import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ArtistCard from "./ArtistCard";
import ExhibitionCarousel from "./ExhibitionCarousel";
import { useApp } from "../context/AppContext";

const Home = () => {
  const { t, lang } = useApp();
  const [artists,        setArtists]        = useState([]);
  const [invitedArtists, setInvitedArtists] = useState([]);
  const [heroSlides,     setHeroSlides]      = useState([]);
  const [heroIdx,        setHeroIdx]         = useState(0);
  const [heroImg,        setHeroImg]         = useState(null);
  const countries = [...new Set(artists.map(a => a.country).filter(Boolean))].length;

  useEffect(() => {
    axios.get("/api/artists").then((r) => setArtists(r.data.artists || [])).catch(() => {});
    axios.get("/api/artists/invited").then((r) => setInvitedArtists(r.data.artists || [])).catch(() => {});
    axios.get("/api/hero-slides").then((r) => setHeroSlides(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (heroSlides.length < 2) return;
    const t = setInterval(() => setHeroIdx(i => (i + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  useEffect(() => {
    const url = heroSlides[heroIdx]?.image_url;
    if (!url) { setHeroImg(null); return; }
    const img = new Image();
    img.onload = () => setHeroImg(url);
    img.onerror = () => setHeroImg(null);
    img.src = url;
  }, [heroIdx, heroSlides]);

  const featured = artists.slice(0, 3);

  return (
    <div>
      {/* ── Hero ── */}
      <section className="hero" style={heroImg ? {
        backgroundImage: `url(${heroImg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 0.8s ease',
      } : {}}>
        <div className="container">
          <span className="eyebrow">{t("heroEyebrow")}</span>
          <h1>{t("heroTitle")}</h1>
          <p>{t("heroSub")}</p>
          <div className="hero-actions">
            <Link className="btn btn-ghost btn-lg" to="/register">
              <i className="fa-solid fa-palette" /> {t("heroCta1")}
            </Link>
            <Link className="btn btn-ghost btn-lg" to="/artists">
              {t("heroCta2")} <i className={`fa-solid fa-arrow-${lang==="ar"?"left":"right"}`} />
            </Link>
          </div>
          <div className="hero-stats">
            <div className="stat"><div className="num">{artists.length || "—"}</div><div className="label">{t("statArtists")}</div></div>
            <div className="stat"><div className="num">100%</div><div className="label">{t("statArt")}</div></div>
            <div className="stat"><div className="num">{countries || "—"}</div><div className="label">{t("statGlobal")}</div></div>
          </div>
        </div>
      </section>

      {/* ── Invited / Pending members ── */}
      {invitedArtists.length > 0 && (
        <section className="section container" style={{ paddingBottom: 0 }}>
          <div className="center" style={{ marginBottom: 32 }}>
            <span className="eyebrow">{t("invitedEyebrow")}</span>
            <h2 className="section-title">{t("invitedTitle")}</h2>
            <p style={{ color: "var(--muted)", maxWidth: 520, margin: "0 auto", lineHeight: 1.6 }}>{t("invitedSub")}</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 14 }}>
            {invitedArtists.map(a => (
              <div key={a._id} style={{
                background: "var(--card)", border: "1px solid var(--line)",
                borderRadius: 12, padding: "20px 12px", textAlign: "center",
              }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--brand-soft)", margin: "0 auto 10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem" }}>
                  ✦
                </div>
                <div style={{ fontWeight: 600, fontSize: "0.88rem", marginBottom: 4 }}>{a.name}</div>
                <div style={{ fontSize: "0.72rem", color: "var(--muted)", background: "var(--brand-soft)", borderRadius: 8, padding: "2px 8px", display: "inline-block" }}>{t("invitedBadge")}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Mission strip ── */}
      <section className="section container">
        <div className="center" style={{ marginBottom:44 }}>
          <span className="eyebrow">{t("whyJoin")}</span>
          <h2 className="section-title">{t("moreThan")}</h2>
        </div>
        <div className="features">
          <div className="feature">
            <div className="ic"><i className="fa-solid fa-id-card" /></div>
            <h3>{t("feature1Title")}</h3>
            <p>{t("feature1Text")}</p>
          </div>
          <div className="feature">
            <div className="ic"><i className="fa-solid fa-people-group" /></div>
            <h3>{t("feature2Title")}</h3>
            <p>{t("feature2Text")}</p>
          </div>
          <div className="feature">
            <div className="ic"><i className="fa-solid fa-hand-holding-heart" /></div>
            <h3>{t("feature3Title")}</h3>
            <p>{t("feature3Text")}</p>
          </div>
        </div>
      </section>

      {/* ── Featured artists ── */}
      <section className="section" style={{ background:"var(--brand-soft)" }}>
        <div className="container">
          <div className="center" style={{ marginBottom:40 }}>
            <span className="eyebrow">{t("featuredEyebrow")}</span>
            <h2 className="section-title">{t("featuredTitle")}</h2>
          </div>
          {featured.length > 0
            ? <div className="artist-grid">{featured.map(a => <ArtistCard key={a._id} artist={a} />)}</div>
            : <div className="empty-state">{t("loading")}</div>
          }
          <div className="center" style={{ marginTop:38 }}>
            <Link className="btn btn-primary btn-lg" to="/artists">{t("seeAll")}</Link>
          </div>
        </div>
      </section>

      {/* ── 30th Exhibition carousel ── */}
      <ExhibitionCarousel />

      {/* ── Gallery teaser ── */}
      <section className="section container">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:32, flexWrap:"wrap", gap:12 }}>
          <div>
            <span className="eyebrow">{t("gallery")}</span>
            <h2 className="section-title" style={{ marginBottom:0 }}>{t("galleryTitle")}</h2>
          </div>
          <Link className="btn btn-outline" to="/gallery">{t("viewAll")}</Link>
        </div>
        <GalleryTeaser />
      </section>

      {/* ── Join CTA ── */}
      <section className="section" style={{ background:"var(--brand)", color:"#fff" }}>
        <div className="container center">
          <span className="eyebrow" style={{ color:"var(--gold)" }}>{t("memberCta")}</span>
          <h2 style={{ color:"#fff", fontSize:"clamp(1.8rem,3vw,2.8rem)", margin:"0 0 14px" }}>{t("memberCtaTitle")}</h2>
          <p style={{ color:"rgba(255,255,255,0.88)", maxWidth:560, margin:"0 auto 28px", lineHeight:1.7 }}>{t("memberCtaSub")}</p>
          <Link className="btn btn-ghost btn-lg" to="/register">{t("becomeAMember")}</Link>
        </div>
      </section>
    </div>

  );
};

// Pulls first 8 real work images for a teaser strip.
function GalleryTeaser() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get("/api/gallery").then(r => setItems((r.data.items || []).slice(0,8))).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:10 }}>
      {items.map((it, i) => (
        <Link to="/gallery" key={i} style={{ display:"block", borderRadius:12, overflow:"hidden", border:"1px solid var(--line)" }}>
          <img src={it.src} alt={it.artistName} style={{ width:"100%", aspectRatio:"1", objectFit:"cover" }} />
        </Link>
      ))}
    </div>
  );
}

export default Home;
